import React, { useState, useEffect, useCallback } from 'react';
import { fmt, api } from '../utils/api';
import CollapsiblePanel from './CollapsiblePanel';

export default function BudgetCalculator({ defaultCollapsed, user, refreshKey }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await api.getSummary();
      setData(d);
    } catch (err) {
      console.error(err);
    }
  }, []);

  // Refetch whenever the budget itself changes, or anything else that
  // could affect the calculation (planned expense reserves, investment
  // goal, etc.) - refreshKey bumps on ANY save anywhere in the app
  useEffect(() => { load(); }, [load, user?.annualBudget, user?.budgetMode, refreshKey]);

  if (!data) return null;

  const b = data.budget.breakdown;
  const dynamic = b.dynamic;

  return (
    <CollapsiblePanel title="🧮 Budget Calculator" defaultCollapsed={defaultCollapsed}>
      <p style={styles.helpText}>
        Exactly how your budget numbers are built, step by step, with real
        figures - not a black box.
      </p>

      <Step
        num={1}
        label="Base Annual Budget"
        value={b.rawAnnualBudget}
        note="The one number you set directly, in Budget Settings (Accounts tab)."
      />

      <Arrow />

      <Step
        num={2}
        label="Reserved for Known Obligations"
        value={-b.reservedTotal}
        negative
        note="Subtracted before anything else - money already spoken for."
      >
        {data.plannedExpenseItems.length > 0 && (
          <div style={styles.itemList}>
            {data.plannedExpenseItems.map((item, i) => (
              <div key={i} style={styles.itemRow}>
                <span style={styles.itemName}>{item.name} <span style={styles.itemFreq}>({item.frequency})</span></span>
                <span style={styles.itemAmt}>{fmt.currency(item.annualizedAmount)}/yr</span>
              </div>
            ))}
          </div>
        )}
        {data.monthlyInvestmentGoal > 0 && (
          <div style={styles.itemList}>
            <div style={styles.itemRow}>
              <span style={styles.itemName}>📈 Investment Contribution Goal</span>
              <span style={styles.itemAmt}>{fmt.currency(data.monthlyInvestmentGoal * 12)}/yr</span>
            </div>
          </div>
        )}
      </Step>

      <Arrow />

      <Step
        num={3}
        label="Adjusted Annual Budget"
        value={b.adjustedAnnualBudget}
        highlight
        note="What's actually available to spend this year, after reserves."
      />

      <Arrow />

      {b.budgetMode === 'static' ? (
        <Step
          num={4}
          label="Monthly Budget (Static Mode)"
          value={b.staticMonthlyBudget}
          note="Adjusted Annual Budget ÷ 12. Flat every month - doesn't change with spending."
        />
      ) : (
        <Step
          num={4}
          label="Monthly Budget (Dynamic Mode)"
          value={b.finalMonthlyBudget}
          note="Recalculates live based on actual spending - see the math below."
        >
          {dynamic && (
            <div style={styles.dynamicMath}>
              <MathRow label="Months with no tracked spending yet (assumed average)" value={`${dynamic.monthsBeforeTracking} mo`} />
              <MathRow label="Assumed spend for those months" value={fmt.currency(dynamic.assumedPriorSpend)} />
              <MathRow label="Actual spend so far this year" value={fmt.currency(dynamic.yearSpendSoFar)} />
              <MathRow label="Spend before this month" value={fmt.currency(dynamic.spendBeforeThisMonth)} />
              <div style={styles.divider} />
              <MathRow label="Adjusted Annual Budget" value={fmt.currency(b.adjustedAnnualBudget)} />
              <MathRow label="− Spend before this month" value={`− ${fmt.currency(dynamic.spendBeforeThisMonth)}`} />
              <MathRow label="= Remaining Budget" value={fmt.currency(dynamic.remainingBudget)} bold />
              <MathRow label="÷ Months remaining (incl. this one)" value={dynamic.monthsRemaining} />
              <MathRow label="= This Month's Budget" value={fmt.currency(b.finalMonthlyBudget)} bold highlight />
            </div>
          )}
        </Step>
      )}

      <Arrow />

      <Step
        num={5}
        label="Weekly Budget"
        value={b.finalWeeklyBudget}
        note="Monthly Budget × 12 ÷ 52 - a simple slice, not tracked independently."
      />

      <p style={styles.footer}>
        {b.budgetMode === 'dynamic'
          ? 'Dynamic mode: overspending early tightens future months, underspending loosens them. Recalculates every time you load the app.'
          : 'Static mode: your monthly budget stays flat regardless of how the year is going.'}
        {' '}Switch modes in Budget Settings (Accounts tab).
      </p>
    </CollapsiblePanel>
  );
}

function Step({ num, label, value, note, negative, highlight, children }) {
  return (
    <div style={{ ...styles.step, ...(highlight ? styles.stepHighlight : {}) }}>
      <div style={styles.stepHeader}>
        <span style={styles.stepNum}>{num}</span>
        <span style={styles.stepLabel}>{label}</span>
        <span style={{ ...styles.stepValue, color: negative ? '#ef4444' : highlight ? '#00d4aa' : '#fff' }}>
          {negative ? '−' : ''}{fmt.currency(Math.abs(value))}
        </span>
      </div>
      {note && <p style={styles.stepNote}>{note}</p>}
      {children}
    </div>
  );
}

function Arrow() {
  return <div style={styles.arrow}>↓</div>;
}

function MathRow({ label, value, bold, highlight }) {
  return (
    <div style={styles.mathRow}>
      <span style={styles.mathLabel}>{label}</span>
      <span style={{
        ...styles.mathValue,
        fontWeight: bold ? '800' : '500',
        color: highlight ? '#00d4aa' : bold ? '#e5e7eb' : '#9ca3af',
      }}>
        {value}
      </span>
    </div>
  );
}

const styles = {
  helpText: {
    color: '#6b7280',
    fontSize: '0.8rem',
    lineHeight: '1.5',
    marginBottom: '1.25rem',
  },
  step: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '0.85rem 1rem',
  },
  stepHighlight: {
    borderColor: '#00d4aa',
    background: 'rgba(0, 212, 170, 0.06)',
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
  },
  stepNum: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    background: '#1e2a3a',
    color: '#9ca3af',
    fontSize: '0.72rem',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontFamily: "'DM Mono', monospace",
  },
  stepLabel: {
    flex: 1,
    color: '#e5e7eb',
    fontSize: '0.85rem',
    fontWeight: '600',
    fontFamily: "'Syne', sans-serif",
  },
  stepValue: {
    fontSize: '1rem',
    fontWeight: '800',
    fontFamily: "'DM Mono', monospace",
  },
  stepNote: {
    color: '#4b5563',
    fontSize: '0.7rem',
    lineHeight: '1.5',
    margin: '0.4rem 0 0 30px',
  },
  itemList: {
    margin: '0.6rem 0 0 30px',
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  itemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.72rem',
  },
  itemName: { color: '#9ca3af', fontFamily: "'Syne', sans-serif" },
  itemFreq: { color: '#4b5563' },
  itemAmt: { color: '#6b7280', fontFamily: "'DM Mono', monospace" },
  arrow: {
    textAlign: 'center',
    color: '#374151',
    fontSize: '0.9rem',
    padding: '2px 0',
  },
  dynamicMath: {
    margin: '0.75rem 0 0 30px',
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.75rem 0.85rem',
  },
  mathRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.72rem',
    padding: '3px 0',
  },
  mathLabel: { color: '#6b7280', fontFamily: "'Syne', sans-serif" },
  mathValue: { fontFamily: "'DM Mono', monospace" },
  divider: { height: '1px', background: '#1e2a3a', margin: '0.4rem 0' },
  footer: {
    color: '#4b5563',
    fontSize: '0.7rem',
    lineHeight: '1.5',
    marginTop: '1rem',
    marginBottom: 0,
    fontStyle: 'italic',
  },
};
